// src/pages/EasyPointServices.jsx

import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useEffect, useState, useMemo } from 'react';
// @mui
import {
  Card,
  Stack,
  Typography,
  TextField,
  FormControl,
  useMediaQuery,
  MenuItem,
  CircularProgress,
  Button,
} from '@mui/material';

// components
import ClearButton from 'src/components/shared/ClearButton';
import FilterButton from 'src/components/shared/FilterButton';
import DynamicTable from 'src/components/shared/DynamicTable';
import CustomFilter from 'src/components/filter/CustomFilter';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import ExportToExcelButton from 'src/components/shared/ExportToExcelButton';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';

export default function EasyPointServices() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const isMobile = useMediaQuery('(max-width:768px)');

  // table ordering & global search
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('no');
  const [filterName, setFilterName] = useState('');

  // data & loading
  const [originalData, setOriginalData] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [loading, setLoading] = useState(false);

  // drawer filters
  const [serviceType, setServiceType] = useState(''); // 'handsfree' | 'gift' | 'pet-car' | 'kid-car'
  const [startDate, setStartDate] = useState(''); // yyyy-MM-dd
  const [endDate, setEndDate] = useState(''); // yyyy-MM-
  const [showPopUpError, setShowPopUpError] = useState(false);
  const [avmName, setAvmName] = useState('');

  // mall dropdown options
  const [malls, setMalls] = useState([]);

  // drawer open/close
  const [filterModal, setFilterModal] = useState(false);
  const handleModal = () => setFilterModal(true);
  const handleCloseModal = () => setFilterModal(false);
  const [statusFilter, setStatusFilter] = useState('');

  // default range: last 30 days (yyyy-MM-dd)
  const today = new Date();
  const defaultEndDate = today.toISOString().split('T')[0];
  const tempStart = new Date(today);
  tempStart.setDate(tempStart.getDate() - 30);
  const defaultStartDate = tempStart.toISOString().split('T')[0];

  // ─── Helpers ────────────────────────────────────────────────────────────

  const formatDateTime = (ts) => {
    const d = new Date(Number(ts) * 1000);
    return (
      `${String(d.getDate()).padStart(2, '0')}.${String(
        d.getMonth() + 1
      ).padStart(2, '0')}.${d.getFullYear()} ` +
      `${String(d.getHours()).padStart(2, '0')}:${String(
        d.getMinutes()
      ).padStart(2, '0')}`
    );
  };

  const mapServiceType = (type) => {
    switch (type) {
      case 'gift':
        return 'Hediye Paketi';
      case 'pet-car':
        return 'Evcil Hayvan Aracı';
      case 'kid-car':
        return 'Çocuk Arabası';
      case 'handsfree-order':
      case 'handsfree-post':
      default:
        return 'Handsfree';
    }
  };

  const hfOrderLabels = {
    1: 'Onaylandı',
    2: 'İptal Edildi',
    3: 'Yanıt Bekleniyor',
    6: 'Müşteri Onayı Bekleniyor',
  };
  const hfPostLabels = {
    1: 'Tamamlandı',
    2: 'İptal Edildi',
    3: 'Beklemede',
    4: 'Porter’da',
    5: 'Şubede',
    6: 'Müşteri Onayı Bekleniyor',
  };
  const statusLabels = {
    1: 'Tamamlandı',
    2: 'İptal',
    99: 'Süre Aşıldı',
    3: 'Onay Bekliyor',
    4: 'Onaylandı',
  };

  const mapStatus = (statusCode, rawType) => {
    if (rawType === 'handsfree-order')
      return hfOrderLabels[statusCode] ?? statusCode;
    if (rawType === 'handsfree-post')
      return hfPostLabels[statusCode] ?? statusCode;
    return statusLabels[statusCode] ?? statusCode;
  };

  const getPriceText = (item) => {
    // ödeme durumu kontrolü
    if (item?.isPaid === '0') {
      return 'Ödeme Bekliyor';
    }
    // hediye veya pet-car türleri
    if (item.type === 'gift' || item.type === 'pet-car' || item.type === 'kid-car') {
      return `${item.price} ${item.currency}`;
    }
    // deliveryTypeInfo üzerinden gelen ödemeler
    if (item.deliveryTypeInfo) {
      const curr =
        item.deliveryTypeInfo.currency === 'TRY'
          ? 'TL'
          : item.deliveryTypeInfo.currency;
      return `${item.deliveryTypeInfo.price} ${curr}`;
    }
    return '';
  };

  function exportToExcel() {
    const temp = servicesList.map((it) => ({
      AVM: it.avm,
      'Hizmet Türü': it.serviceType,
      Tarih: it.date,
      'Miktar/Birim': it.birim,
      İsim: it.user,
      'Teslimat Noktası': it.deliveryPoint,
      Fiyat: it.price,
      Statü: it.status,
    }));

    api
      .post('excel/export', {
        type: 'excel',
        content: temp,
        header: {
          AVM: 'string',
          'Hizmet Türü': 'string',
          Tarih: 'string',
          'Miktar/Birim': 'string',
          İsim: 'string',
          'Teslimat Noktası': 'string',
          Fiyat: 'string',
          Statü: 'string',
        },
        sheetTitle: 'EasyPoint Hizmetleri',
      })
      .then((response) => {
        if (response.data.status) {
          const w = window.open(response.data.result, '_blank');
          if (!w || w.closed || typeof w.closed == 'undefined') {
            setShowPopUpError(true);
            throw new Error('Pop-up engellendi');
          }
        }
      })
      .catch((err) => {
        console.error(err);
        setShowPopUpError(true);
      });
  }

  useEffect(() => {
    setStatusFilter('');
  }, [serviceType]);

  const availableStatuses = useMemo(() => {
    if (!serviceType) {
      return [];
    }
    let labels = {};
    if (serviceType === 'handsfree') {
      // Handsfree için iki label grubunu birleştir
      labels = { ...hfOrderLabels, ...hfPostLabels };
    } else {
      labels = statusLabels;
    }
    // Etiketleri { value, label } formatına çevir
    return Object.entries(labels).map(([key, value]) => ({
      value: value, // Filtreleme metin üzerinden yapılacağı için value'ya metni atıyoruz
      label: value,
    }));
  }, [serviceType]);

  // ─── Fetch & Map ────────────────────────────────────────────────────────

  const fetchServices = async (from, to) => {
    setLoading(true);
    try {
      const res = await Services.GetEPServices(parsedUser.token, from, to);
      console.log(parsedUser.token);
      const list = res.data.result || [];
      const mapped = list.map((it, idx) => {
        const userName = it.name ? `${it.name} ${it.lastname}` : '';
        // compute "birim" column
        let birim = '';
        if (it.type === 'handsfree-post') {
          birim = it.ordersTotalPackageCount
            ? `${it.ordersTotalPackageCount} adet`
            : '';
        } else if (it.type !== 'handsfree-order') {
          if (it.duration === "84000") {
            birim = '1 Gün';
          }
          else {
            if (it.duration) {
              birim = (Number(it.duration) / 3600) + " saat"
            }
            else {
              birim = "-"
            }
          }
        }
        return {
          no: idx + 1,
          user: userName,
          rawType: it.type,
          avm: it.mallTitle,
          serviceType: mapServiceType(it.type),
          date: formatDateTime(it.createdDateTime),
          birim,
          deliveryPoint: it.deliveryTypeInfo?.text || '',
          price: getPriceText(it),
          isPaid: it.isPaid || '',
          status: mapStatus(it.status, it.type),
        };
      });
      setOriginalData(mapped);
      setServicesList(mapped);
      return mapped;
    } catch (err) {
      console.error('Error fetching services', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchMalls = async () => {
    try {
      const res = await Services.GetMalls(parsedUser.token);
      setMalls(res.data.result || []);
    } catch (err) {
      console.error('Error fetching malls', err);
    }
  };

  useEffect(() => {
    fetchServices(defaultStartDate, defaultEndDate);
    fetchMalls();
  }, []);

  // ─── Filtering ──────────────────────────────────────────────────────────

  const getFilteredServiceList = async () => {
    let dataSource = originalData;

    if (startDate || endDate) {
      const from = startDate || defaultStartDate;
      const to = endDate || defaultEndDate;
      dataSource = await fetchServices(from, to);
    }

    const filtered = dataSource.filter((item) => {
      // serviceType filter
      const byType = serviceType
        ? serviceType === 'handsfree'
          ? ['handsfree-order', 'handsfree-post'].includes(item.rawType)
          : item.rawType === serviceType
        : true;
      // avmName (dropdown) filter
      const byAvm = avmName ? item.avm === avmName : true;

      // Status filter
      const byStatus = statusFilter ? item.status === statusFilter : true;

      return byType && byAvm && byStatus; // byStatus koşulunu ekle
    });

    setServicesList(filtered);
    handleCloseModal();
  };

  const clearFilters = async () => {
    setServiceType('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    setAvmName('');
    handleCloseModal();
    await fetchServices(defaultStartDate, defaultEndDate);
  };

  // ─── Sorting + Search ───────────────────────────────────────────────────

  function descendingComparator(a, b, key) {
    if (b[key] < a[key]) return -1;
    if (b[key] > a[key]) return 1;
    return 0;
  }
  function getComparator(order, key) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, key)
      : (a, b) => -descendingComparator(a, b, key);
  }
  function applySortFilter(arr, cmp, query) {
    const stabilized = arr.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
      const comp = cmp(a[0], b[0]);
      if (comp !== 0) return comp;
      return a[1] - b[1];
    });
    const sorted = stabilized.map((el) => el[0]);
    if (query) {
      return filter(
        sorted,
        (row) =>
          row.avm.toLowerCase().includes(query.toLowerCase()) ||
          row.serviceType.toLowerCase().includes(query.toLowerCase())
      );
    }
    return sorted;
  }

  const filteredServices = applySortFilter(
    servicesList,
    getComparator(order, orderBy),
    filterName
  );

  const TABLE_HEAD = [
    { id: 'avm', label: 'AVM', alignRight: false },
    { id: 'serviceType', label: 'Hizmet Türü', alignRight: false },
    { id: 'date', label: 'Tarih', alignRight: false },
    { id: 'birim', label: 'Miktar/Birim', alignRight: false },
    { id: 'user', label: 'İsim', alignRight: false },
    { id: 'deliveryPoint', label: 'Teslimat Noktası', alignRight: false },
    { id: 'price', label: 'Fiyat', alignRight: false },
    { id: 'status', label: 'Statü', alignRight: false },
  ];

  return (
    <>
      <Helmet>
        <title>Easy Point Hizmetleri</title>
      </Helmet>

      <Stack sx={{ px: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" mb={5}>
          <Typography variant="h4">Easy Point Hizmetleri</Typography>
        </Stack>

        {/* Mobile filter toggle */}
        <Stack
          direction="row"
          justifyContent={isMobile ? 'space-between' : 'end'}
          mb={5}
        >
          <ExportToExcelButton
            showPopUpError={showPopUpError}
            setShowPopUpError={setShowPopUpError}
            onClick={exportToExcel}
          />
          {isMobile && <FilterViewButton onClick={handleModal} />}
        </Stack>

        <Card>
          {/* Filter drawer */}
          <CustomFilter
            modalType="drawer"
            filterModal={filterModal}
            handleCloseModal={handleCloseModal}
          >
            <Stack spacing={2} p={2}>
              {/* Hizmet Türü */}
              <FormControl fullWidth>
                <TextField
                  select
                  label="Hizmet Türü"
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="handsfree">Handsfree</MenuItem>
                  <MenuItem value="gift">Hediye Paketi</MenuItem>
                  <MenuItem value="pet-car">Evcil Hayvan Aracı</MenuItem>
                  <MenuItem value="kid-car">Çocuk Arabası</MenuItem>
                </TextField>
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  select
                  label="Statü"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  disabled={!serviceType} // Hizmet türü seçilmeden pasif
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {availableStatuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              {/* Başlangıç & Bitiş Tarihi */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <TextField
                    label="Başlangıç Tarihi"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <TextField
                    label="Bitiş Tarihi"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </FormControl>
              </Stack>

              {/* AVM */}
              <FormControl fullWidth>
                <TextField
                  select
                  label="AVM"
                  value={avmName}
                  onChange={(e) => setAvmName(e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {malls.map((mall) => (
                    <MenuItem key={mall.title} value={mall.title}>
                      {mall.title}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              {/* Buttons */}
              <Stack direction="row" spacing={2}>
                <ClearButton onClick={clearFilters} />
                <FilterButton onClick={getFilteredServiceList} />
              </Stack>
            </Stack>
          </CustomFilter>

          {/* Table or Spinner */}
          {loading ? (
            <Stack alignItems="center" py={5}>
              <CircularProgress />
            </Stack>
          ) : (
            <DynamicTable
              data={filteredServices}
              headers={TABLE_HEAD}
              initialOrder="no"
              initalOrderDirection="asc"
              isOrdered
              isMobile={isMobile}
              handleModal={handleModal}
              handleCloseModal={handleCloseModal}
            />
          )}
        </Card>
      </Stack>
    </>
  );
}
